/* hashtag-list.js */
/* eslint-disable no-console */
import {
  loadRuntimeApiConfig,
  logStagingZetaApiUsage
} from "../config/runtime-api-config";

(() => {
  "use strict";

  const DEFAULTS = {
    endpoint: "",
    initialCount: 15,
    moreCount: 15,
    fetchSize: 200,
    id: ''
    // sort: "desc"
  };

  function toInt(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  function splitTags(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/[,\s]+/)
      .map((tag) => tag.replace(/^#/, "").trim())
      .filter(Boolean);
  }

  function normalizeTagItems(json) {
    const result = json?.result ?? json ?? {};
    const rawItems = Array.isArray(result.items)
      ? result.items
      : Array.isArray(result.hashtags)
      ? result.hashtags
      : Array.isArray(result.list)
      ? result.list
      : Array.isArray(json)
      ? json
      : [];

    const items = rawItems
      .map((item) => {
        if (typeof item === "string") {
          return { tag: item, count: 0 };
        }
        return {
          tag:
            item.tag ??
            item.hashtag ??
            item.name ??
            item.label ??
            "",
          count: Number(item.count ?? item.total ?? 0)
        };
      })
      .filter((item) => item.tag);

    const totalRaw =
      result?.attribute?.total ??
      result?.total ??
      items.length;
    const total = Number(totalRaw);

    return {
      items,
      total: Number.isFinite(total) ? total : items.length
    };
  }

  function encodeTag(tag) {
    return encodeURIComponent(tag.replace(/^#/, "").trim());
  }

  class HashtagList {
    constructor(root, runtimeConfig = {}) {
      this.root = root;
      this.mode = root.dataset.mode || "api-list";

      this.endpoint =
        root.dataset.endpoint ||
        runtimeConfig.hashtagEndpoint ||
        DEFAULTS.endpoint;
      this.initialCount = toInt(root.dataset.initialCount, DEFAULTS.initialCount);
      this.moreCount = toInt(root.dataset.moreCount, DEFAULTS.moreCount);
      this.fetchSize = Math.max(1, toInt(root.dataset.fetchSize, DEFAULTS.fetchSize));
      this.id = root.dataset.id || DEFAULTS.id;
      // this.sort = root.dataset.sort || DEFAULTS.sort;

      this.template = this.resolveTemplate();
      this.listContainer = this.resolveListContainer();
      this.button = this.resolveMoreButton();

      this.renderedCount = 0;
      this.items = [];
      this.fetched = false;
      this.loading = false;

      this.searchPath = root.dataset.tagSearchPath || "/kaiho-biku/search/";
    }

    resolveTemplate() {
      const id = this.root.dataset.templateId;
      if (id) {
        const tpl = document.getElementById(id);
        if (tpl) return tpl;
      }
      return this.root.querySelector("template");
    }

    resolveListContainer() {
      if (this.root.matches("ul,ol")) return this.root;
      return this.root.querySelector("ul,ol") || this.root;
    }

    resolveMoreButton() {
      if (this.root.dataset.moreButtonSelector) {
        return document.querySelector(this.root.dataset.moreButtonSelector);
      }
      if (this.root.id) {
        return document.querySelector(`[data-more-for="${this.root.id}"]`);
      }
      return this.root.parentElement
        ? this.root.parentElement.querySelector(".js-more-button")
        : null;
    }

    tagUrl(tag) {
      const base = this.searchPath;
      const separator = base.includes("?") ? "&" : "?";
      return `${base}${separator}hashtag=${encodeTag(tag)}`;
    }

    buildRequest(length) {
      const params = new URLSearchParams();
      params.set("limit", String(length));
      if (this.id) params.set("id", this.id);
      return `${this.endpoint}?${params.toString()}`;
    }

    async fetchTags(length) {
      if (!this.endpoint) {
        throw new Error("Hashtag API endpoint is not configured");
      }

      logStagingZetaApiUsage(this.endpoint);
      const url = this.buildRequest(length);
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) {
        throw new Error(`Hashtag API failed: ${res.status}`);
      }
      const json = await res.json();
      return normalizeTagItems(json);
    }

    appendUnique(items) {
      const rows = [];
      const seen = new Set();

      items.forEach((item) => {
        const key = String(item.tag).replace(/^#/, "").trim();
        if (!key || seen.has(key)) return;
        seen.add(key);

        rows.push({ ...item, tagLabel: `#${key}`, tagUrl: this.tagUrl(key) });
      });

      return rows;
    }

    bindItem(fragment, item) {
      fragment.querySelectorAll("[data-bind-text]").forEach((el) => {
        const key = el.getAttribute("data-bind-text");
        el.textContent = item[key] != null ? String(item[key]) : "";
      });

      fragment.querySelectorAll("[data-bind-href]").forEach((el) => {
        const key = el.getAttribute("data-bind-href");
        const val = item[key] != null ? String(item[key]) : "#";
        el.setAttribute("href", val);
      });
    }

    appendItems(items) {
      if (!this.template) return;

      const frag = document.createDocumentFragment();
      items.forEach((item) => {
        const node = this.template.content
          ? this.template.content.cloneNode(true)
          : document.importNode(this.template, true);

        this.bindItem(node, item);
        frag.appendChild(node);
      });

      this.listContainer.appendChild(frag);
      this.renderedCount += items.length;
    }

    async ensureFetched() {
      if (this.fetched) return;
      const { items } = await this.fetchTags(this.fetchSize);
      this.items = this.appendUnique(items);
      this.fetched = true;
    }

    updateButton() {
      if (!this.button) return;

      const noMoreByConfig = this.moreCount <= 0;
      const noMoreByData = this.fetched && this.renderedCount >= this.items.length;

      this.button.hidden = noMoreByConfig || noMoreByData;
    }

    async renderBatch(batchSize) {
      if (this.loading || !this.template) return;

      this.loading = true;
      try {
        await this.ensureFetched();
        const start = this.renderedCount;
        const end = start + batchSize;
        const rows = this.items.slice(start, end);
        if (rows.length) this.appendItems(rows);
        this.updateButton();
      } catch (err) {
        console.error("[hashtag-list] render error:", err);
        this.updateButton();
      } finally {
        this.loading = false;
      }
    }

    initInlineTags() {
      const raw = this.root.dataset.tags || "";
      const tags = splitTags(raw);
      const displayCount = Math.max(1, toInt(this.root.dataset.displayCount, tags.length || 1));

      const rows = tags.slice(0, displayCount).map((tag) => ({
        tagLabel: `#${tag}`,
        tagUrl: this.tagUrl(tag)
      }));

      this.appendItems(rows);
      if (this.button) this.button.hidden = true;
    }

    async initApiList() {
      if (!this.endpoint) {
        console.error("[hashtag-list] Hashtag API endpoint is not configured");
        if (this.button) this.button.hidden = true;
        return;
      }

      await this.renderBatch(this.initialCount);
      this.updateButton();

      if (this.button && this.moreCount > 0) {
        this.button.addEventListener("click", () => {
          this.renderBatch(this.moreCount);
        });
      }
    }

    async init() {
      if (!this.template) return;
      if (this.mode === "inline-tags") {
        this.initInlineTags();
        return;
      }
      await this.initApiList();
    }
  }

  async function boot() {
    const runtimeConfig = await loadRuntimeApiConfig();

    document.querySelectorAll(".js-hashtag-list").forEach((root) => {
      const instance = new HashtagList(root, runtimeConfig);
      instance.init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
